/**
 * Validate a hex color string (with or without #).
 */
export function isValidHex(hex: string): boolean {
  return /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(hex);
}

/**
 * Normalize hex to 6-digit format with # prefix.
 */
export function normalizeHex(hex: string): string {
  let h = hex.replace('#', '');
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  return `#${h.toLowerCase()}`;
}

/**
 * Get relative luminance of a hex color (0 = black, 1 = white).
 * Uses WCAG 2.0 formula.
 */
export function getLuminance(hex: string): number {
  const h = normalizeHex(hex).replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;

  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

/**
 * Return 'white' or 'black' depending on which has better contrast
 * against the given background color.
 */
export function getContrastColor(hex: string): 'white' | 'black' {
  return getLuminance(hex) > 0.179 ? 'black' : 'white';
}

/**
 * Convert hex color to space-separated RGB string for Tailwind's <alpha-value> pattern.
 * Input: '#0062ff' → Output: '0 98 255'
 */
export function hexToRgbString(hex: string): string {
  const h = normalizeHex(hex).replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r} ${g} ${b}`;
}

/**
 * Mix two RGB colors by a given ratio.
 * ratio=0 → all colorA, ratio=1 → all colorB
 */
function mixRgb(
  a: [number, number, number],
  b: [number, number, number],
  ratio: number
): [number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * ratio),
    Math.round(a[1] + (b[1] - a[1]) * ratio),
    Math.round(a[2] + (b[2] - a[2]) * ratio),
  ];
}

/**
 * Generate a full 11-shade palette (50–950) from a single hex color.
 * Returns space-separated RGB strings for Tailwind CSS variable compatibility.
 *
 * Lighter shades blend toward white, darker shades blend toward black.
 */
export function generateBrandPalette(hex: string): Record<string, string> {
  const h = normalizeHex(hex).replace('#', '');
  const base: [number, number, number] = [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
  const white: [number, number, number] = [255, 255, 255];
  const black: [number, number, number] = [0, 0, 0];

  const shades: [string, [number, number, number]][] = [
    ['50',  mixRgb(white, base, 0.05)],
    ['100', mixRgb(white, base, 0.15)],
    ['200', mixRgb(white, base, 0.30)],
    ['300', mixRgb(white, base, 0.50)],
    ['400', mixRgb(white, base, 0.70)],
    ['500', base],
    ['600', mixRgb(base, black, 0.10)],
    ['700', mixRgb(base, black, 0.22)],
    ['800', mixRgb(base, black, 0.34)],
    ['900', mixRgb(base, black, 0.50)],
    ['950', mixRgb(base, black, 0.65)],
  ];

  const palette: Record<string, string> = {};
  for (const [shade, rgb] of shades) {
    palette[shade] = `${rgb[0]} ${rgb[1]} ${rgb[2]}`;
  }
  return palette;
}
